import { Component, computed, inject, input } from '@angular/core';
import { LocaleService, TenantService, TranslatePipe } from '@cmi/addin-core';
import { LoadingSpinner, Notification } from '@cmi/ui';
import { swissDateTimeFormat } from '../date-format';
import { folderPath } from '../dossiers/folder-path';
import { FiledTarget } from '../mail-upload/mail-upload.models';
import { FiledDossiersService } from './filed-dossiers.service';

/**
 * Where mails went: dossier, folder and when, each linked into the web client
 * where the tenant has one. Shared by the confirmation of a single mail and of
 * a batch.
 *
 * A target holds ids only, so titles and folder paths are looked up here and
 * show as they are now, not as they were at filing.
 */
@Component({
  selector: 'cmi-filed-targets',
  imports: [TranslatePipe, LoadingSpinner, Notification],
  templateUrl: './filed-targets.html',
  host: { class: 'flex w-full flex-col items-center gap-4' }
})
export class FiledTargets {
  private readonly _localeService = inject(LocaleService);
  private readonly _tenantService = inject(TenantService);

  public readonly targets = input.required<FiledTarget[]>();

  private readonly _dossiers = inject(FiledDossiersService).lookUp(() =>
    this.targets().map((target) => target.dossierGuid)
  );
  protected readonly loading = this._dossiers.loading;

  protected readonly anyUnavailable = computed(() =>
    [...(this._dossiers.dossiers()?.values() ?? [])].includes(null)
  );

  protected readonly entries = computed(() => {
    const dateFormat = swissDateTimeFormat(this._localeService.currentLocale());
    const dossiers = this._dossiers.dossiers();

    return this.targets().map((target) => {
      // Undefined while loading, null when the dossier could not be loaded.
      const dossier = dossiers?.get(target.dossierGuid);
      const path =
        dossier && target.folderGuid
          ? folderPath(dossier.dokumenteExplorer, target.folderGuid)
          : null;

      return {
        filedAt: dateFormat.format(new Date(target.filedAt)),
        dossier,
        dossierLink: this._webClientLink(target.dossierGuid),
        inRoot: target.folderGuid === null,
        folderPath: path ? path.join(' › ') : null,
        // A folder no longer in the tree has nothing to link to.
        folderLink: path ? this._webClientLink(target.folderGuid) : null
      };
    });
  });

  protected onReload(): void {
    this._dossiers.reload();
  }

  /**
   * Null wherever the link cannot be built — the web client is optional on a
   * tenant — and the confirmation then names that target as plain text.
   */
  private _webClientLink(guid: string | null): string | null {
    // Users paste the URL out of their browser, so it can carry the route the
    // deeplink replaces (`/#/dashboard`) or a query — both are dropped here.
    const webClientUrl = this._tenantService.selectedTenant()?.webClientUrl;
    const baseUrl = webClientUrl?.trim().split(/[#?]/)[0].replace(/\/+$/, '');

    return baseUrl && guid ? `${baseUrl}/#/link/${guid.replaceAll('-', '')}` : null;
  }
}
